import React, { useState, useEffect } from 'react'
import dayjs, { Dayjs } from "dayjs";
import 'dayjs/locale/ja'
import { alpha, styled } from '@mui/system';
import { Controller } from 'react-hook-form'
import { Button, InputLabel, InputBase, FormControl, TextField, Chip } from '@mui/material';
import { Box, Typography, InputAdornment, Autocomplete, IconButton } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, Select, MenuItem, FormHelperText, Tooltip } from '@mui/material';
import { DatePicker, TimeField, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ClearIcon from '@mui/icons-material/Clear';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFileOutlined';

// 日付型を日付(YYYY-MM-DD)文字列へ変換
export const toDateFormat = (date) => {
  // Date型でない場合はnullを返す
  if (!(date instanceof Date)) {
    return null;
  }
  // 日付フォーマット
  const formatted = date.toLocaleDateString("ja-JP", {
      year: "numeric", month: "2-digit", day: "2-digit"
    })
    .split("/")
    .join("-");
  return formatted
}

// ISO8601文字列を日付(YYYY-MM-DD)文字列へ変換
export const toDateString = (dateStr) => {
  if (dateStr == null) {
    return null;
  }
  // Date型に変換
  let date = new Date(dateStr);
  // 日付フォーマット
  const formatted = date.toLocaleDateString("ja-JP", {
      year: "numeric", month: "2-digit", day: "2-digit"
    })
    .split("/")
    .join("-");
  return formatted
}

// ISO8601文字列を時刻(HH:mm:dd)文字列へ変換
export const toTimeString = (timeStr) => {
  if (timeStr == null) {
    return null;
  }
  // Date型に変換
  let date = new Date(timeStr);
  // 時刻フォーマット
  return date.toLocaleTimeString('ja-JP')
}

// 時刻(HH:mm:dd)文字列をISO8601文字列へ変換
export const toISOString = (timeStr) => {
  if (timeStr == null) {
    return null;
  }
  if (timeStr.length < 8) {   // 時刻がHH:mm形式の場合、秒を追加
    timeStr = timeStr + ":00";
  }
  let dateStr = new Date().toLocaleDateString('ja-JP') + " " + timeStr
  let date = new Date(dateStr);
  return date.toISOString()
}

export const Container = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '30px',
  backgroundColor: '#fff',
  position: 'absolute',
  top: '70px',
  bottom: '0',
  width: '100vw',
  height: 'calc(100vh - 70px)'
});

export const FormCard = styled('div')({
  backgroundColor: '#fff',
  padding: '20px',
  margin: '0 20px 20px 0',
  borderRadius: '10px',
  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
});

export const ThemeLabel = styled(InputLabel)(({ theme }) => ({
  color: 'black',
  fontSize: 20,
  fontWeight: 'normal',
  '&.Mui-focused': {
    color: theme.palette.primary.main,
  }
}));

export const ThemeInput = styled(InputBase)(({ theme }) => ({
  // ルート自体を横幅一杯に
  width: '100%',
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    position: 'relative',
    backgroundColor: theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
    border: '1px solid #ced4da',
    borderRadius: 4,
    textAlign: "left",
    fontSize: 14,
    // 入力領域も100%
    width: '100%',
    height: 'auto',
    padding: '5px 10px',
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
      borderRadius: 4,
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

export const ThemeFilledText = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  border: '0px',
  borderRadius: 0,
  //height: 48,
  textAlign: "left",
  // 通常時のラベル
  '& label': {
    marginTop: '3px',
    marginLeft: '10px',
    color: 'black',
    fontSize: 16,
  },
  // 通常時のボーダー
  '& .MuiInput-underline:before': {
    borderBottomColor: 'black',
  },
  // ホバー時のボーダー色
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: theme.palette.primary.main,
  },
  // 入力フィールド
  '& .MuiInputBase-input': {
    border: '0px',
    borderRadius: 0,
    fontSize: 14,
    //height: 32,
    marginTop: '0px',
    padding: '5px 5px 5px 10px',  // 上 右 下 左
    '&:focus': {
      border: '0px',
      borderRadius: '0px',
    },
    '&:hover': {
      border: '0px',
      borderRadius: '0px',
    },
  },
}));

export const ThemeUnderlinedLabel = styled(TextField)(({ theme }) => ({
  inputProps: { readonly: true },
  border: '0px',
  borderRadius: 0,
  height: 48,
  // 通常時のラベル
  '& label': {
    marginTop: '3px',
    marginLeft: '10px',
    color: 'black',
    fontSize: 16,
  },
  // 通常時のボーダー
  '& .MuiInput-underline:before': {
    borderBottomColor: 'black',
  },
  // ホバー時のボーダー色
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    //borderBottomColor: theme.palette.primary.main,
    borderBottomColor: 'black',
  },
  // フォーカス時のラベル
  "& label.Mui-focused": {
    color: 'black'
  }, 
  // フォーカス時のボーダー
  "& .MuiInput-underline:after": {
    borderBottomColor: 'black'
  },
  // 入力フィールド
  '& .MuiInputBase-input': {
    border: '0px',
    borderRadius: 0,
    fontSize: 14,
    height: 32,
    padding: '10px 10px 10px 10px',
    '&:focus': {
      border: '0px',
      borderRadius: '0px',
    },
    '&:hover': {
      border: '0px',
      borderRadius: '0px',
    },
  },
}));

export const ThemeOutlinedDate = styled(DatePicker)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    border: '0px',
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputBase-input': {
    position: 'relative',
    border: '0px',
    fontSize: 14,
    height: 48,
    padding: '5px 10px',
  },
}));

export const ThemeFilledTime = styled(TimeField)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  border: '0px',
  borderRadius: 0,
  height: 48,
  // 通常時のラベル
  '& label': {
    marginTop: '3px',
    marginLeft: '10px',
    color: 'black',
    fontSize: 16,
  },
  // 通常時のボーダー
  '& .MuiInput-underline:before': {
    borderBottomColor: 'black',
  },
  // ホバー時のボーダー色
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: theme.palette.primary.main,
  },
  // 入力フィールド
  '& .MuiInputBase-input': {
    border: '0px',
    borderRadius: 0,
    fontSize: 14,
    height: 32,
    padding: '10px 10px 10px 10px',
    '&:focus': {
      border: '0px',
      borderRadius: '0px',
    },
    '&:hover': {
      border: '0px',
      borderRadius: '0px',
    },
  },
}));

export const ThemeLabelText = (props) => {
  return (
    <FormControl variant="standard" fullWidth>
      <ThemeLabel shrink htmlFor={props.id}>
        {props.label}
      </ThemeLabel>
      <ThemeInput 
        id={props.id} 
        type="text" 
        placeholder={props.placeholder} 
        // value が undefined/null のまま後から文字列になると React 警告になるため空文字に正規化
        value={(props.value ?? '')}
        multiline={props.multiline} 
        rows={props.rows} 
        maxRows={props.maxRows} 
        readOnly={true}
      />
    </FormControl>
  );
}

export const ThemeInputText = (props) => {
  const isPassword = props.type === 'password';
  const [show, setShow] = useState(false);
  return (
    <Controller
      control={props.control}
      name={props.id}
      render={({
        field: { onChange, onBlur, value, name, ref },
      }) => (
        <FormControl variant="standard" fullWidth>
          <ThemeLabel shrink htmlFor={props.id}>
            {props.label}
          </ThemeLabel>
          {/* ラベル直後に ThemeInput が来ないため (label + &) セレクタが効かなくなった分の余白をここで再現 */}
          <Box sx={{ position: 'relative', mt: 3 }}>
            <ThemeInput
              onChange={onChange}
              inputRef={ref}
              value={(value ?? '')}
              id={props.id}
              type={isPassword ? (show ? 'text' : 'password') : (props.type || 'text')}
              label={props.label}
              placeholder={props.placeholder}
              multiline={props.multiline}
              rows={props.rows}
              maxRows={props.maxRows}
              autoComplete={props.autoComplete}
              sx={props.endAdornment || isPassword
                ? { '& .MuiInputBase-input': { paddingRight: '38px' } }
                : undefined
              }
            />
            {isPassword && (
              <IconButton
                onClick={() => setShow(s => !s)}
                edge="end"
                size="small"
                tabIndex={-1}
                sx={{ 
                  position: 'absolute', 
                  top: '50%', 
                  right: props.endAdornment ? 36 : 4, 
                  transform: 'translateY(-50%)' 
                }}
              >
                {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            )}
            {props.endAdornment && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 4,
                  transform: 'translateY(-50%)'
                }}
              >
                {props.endAdornment}
              </Box>
            )}
          </Box>
          {props.helperText && (
            <FormHelperText sx={{ ml: 0, mt: 0.5 }} id={`${props.id}-helpertext`}>
              {props.helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

// 単一ファイル選択 (ThemeInputText と同じ見た目、プレビュー/Chipなし)
export const ThemeInputFileSingle = (props) => {
  const inputRef = React.useRef(null);
  const accept = props.accept;
  return (
    <Controller
      control={props.control}
      name={props.id}
      render={({ field: { onChange, value } }) => {
        const fileName = value instanceof File ? (value.name || '') : '';
        // フォームリセット後(値=null)でも input の value が残っていると同じファイル再選択で change が発火しないためクリア
        if (!(value instanceof File) && inputRef.current && inputRef.current.value) {
          try { inputRef.current.value = ''; } catch (_) {}
        }
        return (
          <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
            <ThemeLabel shrink htmlFor={props.id}>{props.label}</ThemeLabel>
            <Box sx={{ position: 'relative', mt: 3 }}>
              <ThemeInput
                id={props.id}
                type="text"
                value={fileName}
                placeholder={props.placeholder || 'ファイル未選択'}
                readOnly
                sx={{ cursor: 'default', '& .MuiInputBase-input': { paddingRight: '40px' } }}
              />
              <Tooltip title="ファイルを選択">
                <IconButton
                  onClick={() => inputRef.current && inputRef.current.click()}
                  size="small"
                  sx={{ position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)', zIndex: 3, background: 'transparent' }}
                >
                  <NoteAddIcon color="primary" />
                </IconButton>
              </Tooltip>
            </Box>
            {props.helperText && (
              <FormHelperText sx={{ ml: 0, mt: 0.5 }} id={`${props.id}-helpertext`}>
                {props.helperText}
              </FormHelperText>
            )}
            <input
              ref={inputRef}
              id={`${props.id}-file-input`}
              type="file"
              style={{ display: 'none' }}
              accept={accept}
              onChange={(e) => {
                const file = (e.target.files && e.target.files[0]) ? e.target.files[0] : null;
                onChange(file);
                props.onFileChange && props.onFileChange(file);
              }}
            />
          </FormControl>
        );
      }}
    />
  );
};

export const ThemeSelectText = (props) => {
  return (
    <FormControl variant="standard" fullWidth>
      <Autocomplete id={props.id} options={props.options} /*value={props.value}*/ freeSolo={true} 
        getOptionLabel={(option) => option.name}
        onChange={props.onChange}
        renderInput={(params) => (
          <FormControl variant="standard" fullWidth ref={params.InputProps.ref}>
            <ThemeLabel shrink htmlFor={props.id} sx={{ marginTop: '3px' }}>{props.label}</ThemeLabel>
            <ThemeInput type="text" placeholder={props.placeholder} {...params.inputProps}/>
          </FormControl>
        )}
        {...props.register(props.id)}
      />
    </FormControl>
  );
}

// テキストフィールド右端のクリア (close) ボタンをオプション化
// showClose=true で従来通り表示 / false で非表示
export const inputSlotProps = (id, setValue, showClose = true) => {
  return ({
    input: {
      startAdornment: <InputAdornment position="start" sx={{m:0}}></InputAdornment>,
      endAdornment: showClose ? (
        <InputAdornment position="end" sx={{mr:'3px', mt:0}}>
          <IconButton onClick={() => setValue(id, "")} size="small">
            <CancelIcon />
          </IconButton>
        </InputAdornment>
      ) : null,
    },
  });
};

export const timeInputProps = {
  startAdornment: <InputAdornment position="start" sx={{mx:0}}></InputAdornment>,
  endAdornment: <InputAdornment position="end" sx={{mx:'3px'}}></InputAdornment>
};

export const StandardTextField = (props) => {
  return (
    <Controller
      control={props.control}
      name={props.id}
      render={({
        field: { onChange, onBlur, value, name, ref },
      }) => (
        <ThemeFilledText 
          onChange={onChange}
          inputRef={ref}
          value={(value ?? '')}
          label={props.label}
          variant="standard"
          multiline={props.multiline}
          rows={props.rows}
          maxRows={props.maxRows}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start" sx={{m:0}}></InputAdornment>,
              endAdornment: (props.endAdornment || props.showClose) ? (
                <InputAdornment position="end" sx={{m:0}}>
                  {props.endAdornment && props.endAdornment}
                  {props.showClose === true && (
                    <IconButton onClick={() => props.setValue && props.setValue(props.id, '')} size="small">
                      <CancelIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ) : null,
            }
          }}
        />
      )}
    />
  );
}

export const OutlinedDatePicker = (props) => {
  return (
    <Controller
      control={props.control}
      name={props.id}
      render={({
        field: { onChange, onBlur, value, name, ref },
      }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
          <ThemeOutlinedDate
            onChange={onChange}
            inputRef={ref}
            value={value ? dayjs(value) : null}
            label={props.label}
            variant="standard" 
            control={props.control}
            slotProps={{field: {clearable: true, focused: true}}}
          />
        </LocalizationProvider>
      )}
    />
  );
}

export const StandardTimeField = (props) => {
  return (
    <Controller
      control={props.control}
      name={props.id}
      render={({
        field: { onChange, onBlur, value, name, ref },
      }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
          <ThemeFilledTime
            onChange={onChange}
            inputRef={ref}
            value={value ? dayjs(value) : null}
            label={props.label}
            variant="standard"
            format="HH:mm:ss"
            control={props.control}
            clearable={true} InputProps={timeInputProps}
          />
        </LocalizationProvider>
      )}
    />
 );
}

// 共通DatePickerコンポーネント（StandardTimePickerと同じデザイン）
export const StandardDatePicker = ({ id, label, control, disabled, showClose }) => (
  <Controller
    name={id}
    control={control}
    render={({ field }) => (
      <DatePicker
        label={label}
        disabled={disabled}
        value={field.value ? dayjs(field.value) : null}
        onChange={val => field.onChange(val ? val.format('YYYY-MM-DD') : null)}
        format="YYYY-MM-DD"
        slotProps={{
          textField: (params) => ({
            ...params,
            fullWidth: true,
            variant: 'standard',
            placeholder: 'yyyy-mm-dd',
            InputLabelProps: { shrink: true },
            InputProps: {
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {params.InputProps?.endAdornment}
                  {showClose && (
                    <IconButton size="small" onClick={() => field.onChange(null)}>
                      <CancelIcon/>
                    </IconButton>
                  )}
                </>
              )
            },            
            sx: theme => ({
              backgroundColor: theme.palette.secondary.light,
              borderBottom: '1px solid black',
              '& label': { color: 'black', paddingLeft: '12px', marginTop: '3px' },
              '& .MuiInputBase-input': { paddingLeft: '10px', fontSize: '14px' },
              '&:hover': {
                border: 'none',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                boxShadow: 'none',
                outline: 'none',
                borderRadius: 0,
              },
              '& .MuiInputBase-root': {
                border: 'none',
                boxShadow: 'none',
                outline: 'none',
                borderRadius: 0,
              },
              '& .MuiInputBase-root:after': {
                borderBottom: '2px solid black',
              },
              '& .MuiInputLabel-root': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              '& .MuiInputLabel-root:hover': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              '& .Mui-focused': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              '& input:focus': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              '& input:hover': {
                outline: 'none',
                boxShadow: 'none',
                border: 'none',
              },
              // カレンダーボタン（InputAdornment）を左へ10pxずらす
              '& .MuiInputAdornment-root': {
                marginRight: '10px',
              }
            })
          })
        }}
      />
    )}
  />
);

export const ThemeTimePicker = ({ id, label, control, showSeconds = false }) => (
  <Controller
    name={id}
    control={control}
    render={({ field }) => {
      const views = showSeconds ? ['hours','minutes','seconds'] : ['hours','minutes'];
      const format = showSeconds ? 'HH:mm:ss' : 'HH:mm';

      return (
        <FormControl
          variant="standard"
          fullWidth
          sx={{
            /* フォーカス時にラベルの色をテーマ色に */
            "&:focus-within .MuiInputLabel-root": {
              color: (theme) => theme.palette.primary.main
            }
          }}
        >
          <ThemeLabel shrink htmlFor={id}>
            {label}
          </ThemeLabel>

          <Box sx={{ mt: 3 }}>
            <TimePicker
              value={field.value ? dayjs(field.value, format) : null}
              onChange={(val) => field.onChange(val ? val.format(format) : '')}
              ampm={false}
              views={views}
              timeSteps={{ minutes: 1 }}
              slotProps={{
                textField: {
                  id,
                  fullWidth: true,
                  variant: "standard",
                  InputProps: { disableUnderline: true },
                  sx: (theme) => ({
                    backgroundColor:
                      theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
                    border: '1px solid #ced4da',
                    borderRadius: 1,
                    "&:hover": { borderColor: theme.palette.primary.main },
                    "& .MuiInputBase-root.Mui-focused": {
                      boxShadow: `${theme.palette.primary.main}40 0 0 0 0.2rem`,
                      borderRadius: 1
                    },
                    "& .MuiInputBase-input": {
                      padding: '5px 10px',
                      fontSize: 14,
                      textAlign: "left"
                    },
                    "& .MuiInputAdornment-root": { marginRight: "8px" },
                    "& .MuiInput-underline:before": { display: "none" },
                    "& .MuiInput-underline:after": { display: "none" }
                  })
                }
              }}
            />
          </Box>
        </FormControl>        
      );
    }}
  />
);

// 共通TimePickerコンポーネント
export const StandardTimePicker = ({ id, label, control, disabled, showSeconds = false, showClose = false }) => (
  <Controller
    name={id}
    control={control}
    render={({ field }) => {
      // 秒まで表示するかどうかでviews/format/placeholder/値変換を切り替え
      const views = showSeconds ? ["hours", "minutes", "seconds"] : ["hours", "minutes"];
      const format = showSeconds ? "HH:mm:ss" : "HH:mm";
      const placeholder = showSeconds ? "hh:mm:ss" : "hh:mm";
      return (
        <TimePicker
          label={label}
          disabled={disabled}
          value={field.value ? dayjs(field.value, format) : null}
          onChange={val => field.onChange(val ? val.format(format) : null)}
          ampm={false}
          timeSteps={{ minutes: 1 }}
          views={views}
          slotProps={{
            textField: (params) => ({
              ...params,
              fullWidth: true,
              variant: 'standard',
              placeholder,
              InputLabelProps: { shrink: true },
              InputProps: {
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {params.InputProps?.endAdornment}
                    {showClose && (
                      <IconButton size="small" onClick={() => field.onChange(null)}>
                        <CancelIcon/>
                      </IconButton>
                    )}
                  </>
                )
              },
              sx: theme => ({
                backgroundColor: theme.palette.secondary.light,
                borderBottom: '1px solid black',
                '& label': { color: 'black', paddingLeft: '12px', marginTop: '3px' },
                '& .MuiInputBase-input': { paddingLeft: '10px', fontSize: '14px' },
                '&:hover': {
                  border: 'none',
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                  boxShadow: 'none',
                  outline: 'none',
                  borderRadius: 0,
                },
                '& .MuiInputBase-root': {
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                  borderRadius: 0,
                },
                '& .MuiInputBase-root:after': {
                  borderBottom: '2px solid black',
                },
                '& .MuiInputLabel-root': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiInputLabel-root:hover': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .Mui-focused': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& input:focus': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& input:hover': {
                  outline: 'none',
                  boxShadow: 'none',
                  border: 'none',
                },
                // 時計ボタン（InputAdornment）を左へ10pxずらす
                '& .MuiInputAdornment-root': {
                  marginRight: '10px',
                }
              })
            })
          }}
        />
      );
    }}
  />
);

export const UnderlinedLabel = (props) => {
  return (
    <ThemeUnderlinedLabel 
      id={props.id} 
      label={props.label} 
      // 未定義のまま後で値が付く警告防止
      value={(props.value ?? '')}
      variant="standard" 
      {...props.register(props.id)}
    />
  );
}

// ThemeInputのデザインを継承したBox
const ThemeInputBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.mode === 'light' ? '#fcfcfb' : '#2b2b2b',
  border: '1px solid #ced4da',
  borderRadius: 4,
  textAlign: 'left',
  fontSize: 14,
  width: '100%',
  minHeight: 36,
  padding: '5px 10px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1,
  '&:focus-within': {
    boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
    borderColor: theme.palette.primary.main,
    borderRadius: 4,
    outline: 'none',
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

// テキストプレビュー用：UTF-8優先でデコードし、文字化けの兆候があればShift_JISにフォールバック
async function readTextPreviewSmart(file, limitBytes) {
  const maxBytes = (limitBytes ?? 200 * 1024);
  const blob = file.slice(0, maxBytes);
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);

  const decode = (encoding) => {
    try {
      const dec = new TextDecoder(encoding, { fatal: false });
      return dec.decode(bytes);
    } catch (e) {
      return null;
    }
  };

  // 1) BOMチェック
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
    const text = decode('utf-8') ?? '';
    return { text, encodingUsed: 'utf-8' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
    const text = decode('utf-16le') ?? '';
    return { text, encodingUsed: 'utf-16le' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
    const text = decode('utf-16be') ?? '';
    return { text, encodingUsed: 'utf-16be' };
  }

  // 2) UTF-8を試す
  let text = decode('utf-8');
  let encodingUsed = 'utf-8';
  const hasReplacement = text ? text.includes('\uFFFD') : false;
  // 高ビットの比率と拡張子で簡易推定
  let highBit = 0;
  for (const b of bytes) if (b >= 0x80) highBit++;
  const highBitRatio = bytes.length ? highBit / bytes.length : 0;
  const name = (file.name || '').toLowerCase();
  const likelySjisExt = /\.(csv|tsv|txt|log)$/i.test(name);

  if (hasReplacement || (highBitRatio > 0.2 && likelySjisExt)) {
    const sjis = decode('shift_jis');
    if (sjis) {
      // 日本語文字の出現数で素朴に比較
      const jpCount = (s) => (s.match(/[\u3040-\u30ff\u3400-\u9fff]/g) || []).length;
      if (!text || jpCount(sjis) >= jpCount(text)) {
        text = sjis;
        encodingUsed = 'shift_jis';
      }
    }
    // EUC-JPも試す（ブラウザのサポート状況による）。対応していない場合はnull
    const euc = decode('euc-jp');
    if (euc) {
      const jpCount = (s) => (s.match(/[\u3040-\u30ff\u3400-\u9fff]/g) || []).length;
      if (jpCount(euc) > jpCount(text)) {
        text = euc;
        encodingUsed = 'euc-jp';
      }
    }
  }

  if (file.size > maxBytes) {
    text += `\n\n...（${Math.ceil((file.size - maxBytes) / 1024)}KB 以降は省略）`;
  }
  return { text, encodingUsed };
}

// ファイル種別の推定（image / pdf / text / other）
function getFileKind(file) {
  let name = '';
  let type = '';
  if (file instanceof File) {
    name = file.name || '';
    type = file.type || '';
  } else if (file && typeof file === 'object') {
    name = file.name || file.file_name || file.filename || '';
    type = file.type || '';
  } else if (typeof file === 'string') {
    name = file;
  }
  name = (name || '').toLowerCase();
  type = (type || '').toLowerCase();
  const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
  const isImage = type.startsWith('image/') || /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(name);
  const isText = type.startsWith('text/') || /\.(txt|csv|json|md|log|xml|yml|yaml|js|ts|css|html)$/.test(name);
  if (isPdf) return 'pdf';
  if (isImage) return 'image';
  if (isText) return 'text';
  return 'other';
}

// 種別別のアイコン
function getFileIcon(kind) {
  switch (kind) {
    case 'image':
      return <ImageIcon fontSize="small" sx={{ color: (theme) => theme.palette.primary.main }} />;
    case 'pdf':
      return <PictureAsPdfIcon fontSize="small" sx={{ color: (theme) => theme.palette.error.main }} />;
    case 'text':
      return <DescriptionIcon fontSize="small" sx={{ color: (theme) => theme.palette.success.main }} />;
    default:
      return <InsertDriveFileIcon fontSize="small" sx={{ color: (theme) => theme.palette.text.disabled }} />;
  }
}

// アイコン色（Chipの内部クラスにも適用して確実に反映）
function getFileColor(kind) {
  return (theme) => {
    switch (kind) {
      case 'image':
        return theme.palette.primary.main;
      case 'pdf':
        return theme.palette.error.main;
      case 'text':
        return theme.palette.success.main;
      default:
        return theme.palette.text.disabled;
    }
  };
}

// ファイル選択コンポーネント
export const ThemeSelectFile = (props) => {
  // フォーカス管理
  const [focused, setFocused] = React.useState(false);  
  // input参照（複数設置時のID競合回避＆クリック制御）
  const inputRef = React.useRef(null);
  const inputBoxRef = React.useRef(null);
  // ビューア（画像/PDF/テキスト）
  const [viewer, setViewer] = React.useState({ open: false, kind: '', url: '', text: '', encoding: 'auto', file: null });
  // ファイル選択
  const onFileInputChange = (e) => {
    if (!e.target.files) return;
    const current = Array.isArray(props.files) ? [...props.files] : [];
    // 追加候補
    let added = Array.from(e.target.files);
    // サイズ制限
    if (props.maxSizeMB) {
      const limit = props.maxSizeMB * 1024 * 1024;
      const filtered = added.filter(f => f.size <= limit);
      if (filtered.length !== added.length) {
        props.onError && props.onError(`サイズ超過のファイルを除外しました（最大 ${props.maxSizeMB} MB）`);
      }
      added = filtered;
    }
    // 件数制限
    if (props.maxFiles && current.length + added.length > props.maxFiles) {
      const allow = Math.max(0, props.maxFiles - current.length);
      if (allow < added.length) {
        props.onError && props.onError(`選択可能なファイル数は最大 ${props.maxFiles} 件です`);
      }
      added = added.slice(0, allow);
    }
    // 重複除外（name+size+lastModified ベース）
    const keyOf = f => (f instanceof File) ? `${f.name}-${f.size}-${f.lastModified}` : `other-${Math.random()}`;
    const existing = new Set(current.filter(f => f instanceof File).map(keyOf));
    const merged = [...current];
    for (const f of added) {
      const k = keyOf(f);
      if (!existing.has(k)) {
        merged.push(f);
        if (f instanceof File) existing.add(k);
      }
    }
    props.setFiles(merged);
    // 同一ファイルの再選択を許可するために値をクリア
    if (inputRef.current) inputRef.current.value = '';
  };
  // ファイルクリア
  const onFileInputClear = (e) => {
    props.setFiles([]);
    if (inputRef.current) inputRef.current.value = '';
  };
  // D&D 対応
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dt = e.dataTransfer;
    if (!dt || !dt.files) return;
    const fakeEvent = { target: { files: dt.files } };
    onFileInputChange(fakeEvent);
  };
  // プレビューを開く
  const openViewer = async (file) => {
    if (!(file instanceof File)) return;
    const name = (file.name || '').toLowerCase();
    const type = file.type || '';
    const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
    const isImage = type.startsWith('image/') || /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
    const isText = type.startsWith('text/') || /\.(txt|csv|json|md|log)$/i.test(name);
    if (isImage || isPdf) {
      const url = URL.createObjectURL(file);
      setViewer({ open: true, kind: isPdf ? 'pdf' : 'image', url, text: '', encoding: 'auto', file });
      return;
    }
    if (isText) {
      const { text, encodingUsed } = await readTextPreviewSmart(file, props.textPreviewMaxBytes);
      setViewer({ open: true, kind: 'text', url: '', text, encoding: encodingUsed, file });
      return;
  }
  // その他は即ダイアログ（ダウンロードボタンで保存）
  setViewer({ open: true, kind: 'other', url: '', text: '', encoding: 'auto', file });
  };
  // プレビューを閉じる
  const closeViewer = () => {
    if (viewer.url) URL.revokeObjectURL(viewer.url);
    setViewer({ open: false, kind: '', url: '', text: '', encoding: 'auto', file: null });
  };
  // ダウンロード機能
  const downloadViewerFile = () => {
    const f = viewer.file;
    if (!(f instanceof File)) return;
    const url = URL.createObjectURL(f);
    const a = document.createElement('a');
    a.href = url;
    a.download = f.name || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  // 再デコード
  const redecode = async (file, encoding) => {
    const maxBytes = (props.textPreviewMaxBytes ?? 200 * 1024);
    const blob = file.slice(0, maxBytes);
    const buf = await blob.arrayBuffer();
    let text = '';
    try {
      text = new TextDecoder(encoding, { fatal: false }).decode(new Uint8Array(buf));
    } catch (e) {
      text = viewer.text; // 失敗したら元に戻す
    }
    if (file.size > maxBytes) {
      text += `\n\n...（${Math.ceil((file.size - maxBytes) / 1024)}KB 以降は省略）`;
    }
    setViewer((v) => ({ ...v, text, encoding }));
  };
  // レンダリング
  return (
    <FormControl variant="standard" fullWidth sx={{ position: 'relative' }}>
      <ThemeLabel
        shrink
        htmlFor={props.id}
        sx={theme => ({
          position: 'absolute',
          zIndex: 3,
          background: theme.palette.mode === 'light' ? '#fff' : '#2b2b2b',
          px: 0.5,
          fontSize: 20,
          color: focused ? theme.palette.primary.main : 'black',
          transition: 'color 0.2s',
        })}
        onClick={() => inputBoxRef.current && inputBoxRef.current.focus()}
      >
        {props.label}
      </ThemeLabel>
      <Box sx={{ position: 'relative', mt: 3 }}>
        <ThemeInputBox
          tabIndex={0}
          sx={{ pr: 8, maxHeight: 150, overflowY: 'auto' }}
          ref={inputBoxRef}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {props.files && props.files.length > 0 ? (
            props.files.map((file, idx) => (
              <Chip
                key={idx}
                label={(file && (file.name || file.file_name)) ? (file.name || file.file_name) : String(file)}
                size="small"
                sx={{ fontSize: 13, '& .MuiChip-icon': { color: (theme) => getFileColor(getFileKind(file))(theme) } }}
                icon={getFileIcon(getFileKind(file))}
                onDelete={() => {
                  const newFiles = props.files.filter((_, i) => i !== idx);
                  props.setFiles(newFiles);
                }}
                onClick={() => openViewer(file)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
              {props.placeholder}
            </Typography>
          )}
        </ThemeInputBox>
        {/* プレビューダイアログ */}
        <Dialog open={viewer.open} onClose={closeViewer} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            プレビュー
            {viewer.kind === 'text' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Encoding</Typography>
                <Select size="small" value={viewer.encoding || 'utf-8'} onChange={(e) => {
                  const sel = e.target.value;
                  const target = (props.files || []).find(f => f instanceof File && f.name && viewer.text);
                  const file = target || null;
                  if (file) redecode(file, sel);
                  else setViewer(v => ({ ...v, encoding: sel }));
                }}>
                  <MenuItem value="utf-8">UTF-8</MenuItem>
                  <MenuItem value="shift_jis">Shift_JIS</MenuItem>
                  <MenuItem value="euc-jp">EUC-JP</MenuItem>
                  <MenuItem value="utf-16le">UTF-16LE</MenuItem>
                  <MenuItem value="utf-16be">UTF-16BE</MenuItem>
                </Select>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={downloadViewerFile} size="small" disabled={!(viewer.file instanceof File)}>
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={closeViewer} size="small"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {viewer.kind === 'image' && (
              <Box component="img" src={viewer.url} alt="preview" sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain' }} />
            )}
            {viewer.kind === 'pdf' && (
              <Box component="iframe" src={viewer.url} title="pdf" sx={{ display: 'block', width: '100%', height: '85vh', border: 0 }} />
            )}
            {viewer.kind === 'text' && (
              <Box component="pre" sx={{ m: 0, p: 2, width: '100%', maxHeight: '85vh', overflow: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{viewer.text}</Box>
            )}
            {viewer.kind === 'other' && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2">プレビュー未対応の種類です。右上のダウンロードボタンから保存できます。</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <Box sx={{ position: 'absolute', top: 2, right: 8, display: 'flex', alignItems: 'center', gap: 0.5, zIndex: 2 }}>
          <Tooltip title="ファイルを選択">
            <IconButton onClick={() => inputRef.current && inputRef.current.click()} size="small">
              <NoteAddIcon color="primary"/>
            </IconButton>
          </Tooltip>
          <IconButton onClick={onFileInputClear} size="small">
            <ClearIcon color="primary"/>
          </IconButton>
          <VisuallyHiddenInput
            ref={inputRef}
            id={`${props.id || 'file'}-input-hidden`}
            type="file"
            accept={props.accept}
            onChange={onFileInputChange}
            multiple
          />
        </Box>
      </Box>
    </FormControl>
  );
}

// ファイル選択コンポーネント
export const StandardSelectFile = (props) => {
  // フォーカス管理
  const [focused, setFocused] = React.useState(false);
  // input参照（複数設置時のID競合回避＆クリック制御）
  const inputRef = React.useRef(null);
  // チップ領域参照 & レイアウト状態
  const chipAreaRef = React.useRef(null);
  const [multiLine, setMultiLine] = React.useState(false);
  // ビューア（画像/PDF/テキスト）
  const [viewer, setViewer] = React.useState({ open: false, kind: '', url: '', text: '', encoding: 'auto', file: null });
  // ファイル選択
  const onFileInputChange = (e) => {
    if (!e.target.files) return;
    const current = Array.isArray(props.files) ? [...props.files] : [];
    // 追加候補
    let added = Array.from(e.target.files);
    // サイズ制限
    if (props.maxSizeMB) {
      const limit = props.maxSizeMB * 1024 * 1024;
      const filtered = added.filter(f => f.size <= limit);
      if (filtered.length !== added.length) {
        props.onError && props.onError(`サイズ超過のファイルを除外しました（最大 ${props.maxSizeMB} MB）`);
      }
      added = filtered;
    }
    // 件数制限
    if (props.maxFiles && current.length + added.length > props.maxFiles) {
      const allow = Math.max(0, props.maxFiles - current.length);
      if (allow < added.length) {
        props.onError && props.onError(`選択可能なファイル数は最大 ${props.maxFiles} 件です`);
      }
      added = added.slice(0, allow);
    }
    // 重複除外（name+size+lastModified ベース）
    const keyOf = f => (f instanceof File) ? `${f.name}-${f.size}-${f.lastModified}` : `other-${Math.random()}`;
    const existing = new Set(current.filter(f => f instanceof File).map(keyOf));
    const merged = [...current];
    for (const f of added) {
      const k = keyOf(f);
      if (!existing.has(k)) {
        merged.push(f);
        if (f instanceof File) existing.add(k);
      }
    }
    props.setFiles(merged);
    // 同一ファイルの再選択を許可するために値をクリア
    if (inputRef.current) inputRef.current.value = '';
  };
  // ファイルクリア
  const onFileInputClear = (e) => {
    props.setFiles([]);
    if (inputRef.current) inputRef.current.value = '';
  };
  // D&D 対応
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dt = e.dataTransfer;
    if (!dt || !dt.files) return;
    const fakeEvent = { target: { files: dt.files } };
    onFileInputChange(fakeEvent);
  };
  // プレビューを開く
  const openViewer = async (file) => {
    if (!(file instanceof File)) return;
    const name = (file.name || '').toLowerCase();
    const type = file.type || '';
    const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
    const isImage = type.startsWith('image/') || /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
    const isText = type.startsWith('text/') || /\.(txt|csv|json|md|log)$/i.test(name);
    if (isImage || isPdf) {
      const url = URL.createObjectURL(file);
      setViewer({ open: true, kind: isPdf ? 'pdf' : 'image', url, text: '', encoding: 'auto', file });
      return;
    }
    if (isText) {
      const { text, encodingUsed } = await readTextPreviewSmart(file, props.textPreviewMaxBytes);
      setViewer({ open: true, kind: 'text', url: '', text, encoding: encodingUsed, file });
      return;
    }
    // その他は即ダイアログ（ダウンロードボタンで保存）
    setViewer({ open: true, kind: 'other', url: '', text: '', encoding: 'auto', file });
  };
  // プレビューを閉じる
  const closeViewer = () => {
    if (viewer.url) URL.revokeObjectURL(viewer.url);
    setViewer({ open: false, kind: '', url: '', text: '', encoding: 'auto', file: null });
  };
  // ダウンロード機能
  const downloadViewerFile = () => {
    const f = viewer.file;
    if (!(f instanceof File)) return;
    const url = URL.createObjectURL(f);
    const a = document.createElement('a');
    a.href = url;
    a.download = f.name || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  // 再デコード
  const redecode = async (file, encoding) => {
    const maxBytes = (props.textPreviewMaxBytes ?? 200 * 1024);
    const blob = file.slice(0, maxBytes);
    const buf = await blob.arrayBuffer();
    let text = '';
    try {
      text = new TextDecoder(encoding, { fatal: false }).decode(new Uint8Array(buf));
    } catch (e) {
      text = viewer.text; // 失敗したら元に戻す
    }
    if (file.size > maxBytes) {
      text += `\n\n...（${Math.ceil((file.size - maxBytes) / 1024)}KB 以降は省略）`;
    }
    setViewer((v) => ({ ...v, text, encoding }));
  };  
  // チップ領域が複数行か判定
  const updateChipLines = React.useCallback(() => {
    // チップ領域の参照を取得
    const area = chipAreaRef.current;
    if (!area) { setMultiLine(false); return; }
    // チップ要素を取得
    const chips = area.querySelectorAll('.MuiChip-root');
    if (!chips.length) { setMultiLine(false); return; }
    // offsetTopを集めて行数を判定
    const tops = new Set();
    chips.forEach(ch => tops.add(ch.offsetTop));
    setMultiLine(tops.size > 1);
  }, []);

  // 初期 & ファイル変更後に計測（レイアウト確定後の遅延）
  React.useEffect(() => {
    const id = setTimeout(updateChipLines, 0);
    return () => clearTimeout(id);
  }, [props.files, updateChipLines]);

  // ウィンドウリサイズ時に計測
  React.useEffect(() => {
    const onResize = () => updateChipLines();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updateChipLines]);

  // レンダリング
  return (
    <FormControl variant="standard" fullWidth>
      <Box
        tabIndex={0}
        sx={theme => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 48,
          borderBottom: '1px solid #000',
          background: theme.palette.secondary.light,
          px: 1,
          py: 1,
          transition: 'border-bottom 0.2s',
          '&:hover': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
          },
          // フォーカス時の黒いアウトライン除去 & 下線のみ色変更
          '&:focus': {
            outline: 'none',
            borderBottom: `2px solid ${theme.palette.primary.main}`,
          },
          '&:focus-visible': {
            outline: 'none',
            borderBottom: `2px solid ${theme.palette.primary.main}`,
          },
        })}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <ThemeLabel
          shrink
          htmlFor={props.id}
          sx={theme => ({
            fontSize: 16,
            marginLeft: '8px',
            marginTop: '3px',
            color: focused ? theme.palette.primary.main : 'black',
            transition: 'color 0.2s',
            background: theme.palette.secondary.light,
            zIndex: 3,
            p: 0,
          })}
        >
          {props.label}
        </ThemeLabel>
        {/* 上部右側: 1行時 追加+クリア / 複数行時 クリアのみ */}
        <Box sx={{
          position: 'absolute',
          top: 4,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 10,
          background: theme => theme.palette.secondary.light,
          pt: 0.5,
        }}>
          {!multiLine && (
            <Tooltip title="ファイルを追加">
              <IconButton onClick={() => inputRef.current && inputRef.current.click()} size="small">
                <NoteAddIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onFileInputClear} size="small">
            <CancelIcon />
          </IconButton>
        </Box>
        {/* 複数行時: 追加ボタンを右下に分離配置 */}
        {multiLine && (
          <Box sx={{
            position: 'absolute',
            right: 0,
            bottom: 4,
            zIndex: 10,
            background: theme => theme.palette.secondary.light,
            pb: 0.5,
            display: 'flex',
          }}>
            <Tooltip title="ファイルを追加">
              <IconButton onClick={() => inputRef.current && inputRef.current.click()} size="small">
                <NoteAddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <VisuallyHiddenInput
          ref={inputRef}
          id={`${props.id || 'file'}-input-hidden`}
          type="file"
          accept={props.accept}
          onChange={onFileInputChange}
          multiple
        />
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          // チップ領域のみ高さ制限。超えた場合のみスクロールバー表示
          overflowY: 'auto',
          maxHeight: 193,
          mt: 1.5,
          // 右側アイコン領域を物理的に確保: width を縮め margin-right を利用
          width: `calc(100% - ${multiLine ? 30 : 60}px)`,
          mr: `${multiLine ? 30 : 60}px`,
          pb: 0,
          transition: 'width 0.2s ease, max-height 0.2s ease',
          // フォーカス時の黒いアウトライン除去
          '&:focus': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: 'none',
          },
        }} ref={chipAreaRef}>
          {props.files && props.files.length > 0 ? (
            props.files.map((file, idx) => (
              <Chip
                key={idx}
                label={(file && (file.name || file.file_name)) ? (file.name || file.file_name) : String(file)}
                size="small"
                sx={{ fontSize: 13, '& .MuiChip-icon': { color: (theme) => getFileColor(getFileKind(file))(theme) } }}
                icon={getFileIcon(getFileKind(file))}
                onDelete={() => {
                  const newFiles = props.files.filter((_, i) => i !== idx);
                  props.setFiles(newFiles);
                }}
                onClick={() => openViewer(file)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>
              {props.placeholder}
            </Typography>
          )}
        </Box>
        {/* プレビューダイアログ */}
        <Dialog open={viewer.open} onClose={closeViewer} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            プレビュー
            {viewer.kind === 'text' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Encoding</Typography>
                <Select size="small" value={viewer.encoding || 'utf-8'} onChange={(e) => {
                  const sel = e.target.value;
                  const target = (props.files || []).find(f => f instanceof File && f.name && viewer.text);
                  const file = target || null;
                  if (file) redecode(file, sel);
                  else setViewer(v => ({ ...v, encoding: sel }));
                }}>
                  <MenuItem value="utf-8">UTF-8</MenuItem>
                  <MenuItem value="shift_jis">Shift_JIS</MenuItem>
                  <MenuItem value="euc-jp">EUC-JP</MenuItem>
                  <MenuItem value="utf-16le">UTF-16LE</MenuItem>
                  <MenuItem value="utf-16be">UTF-16BE</MenuItem>
                </Select>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={downloadViewerFile} size="small" disabled={!(viewer.file instanceof File)}>
                <DownloadIcon />
              </IconButton>
              <IconButton onClick={closeViewer} size="small"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {viewer.kind === 'image' && (
              <Box component="img" src={viewer.url} alt="preview" sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain' }} />
            )}
            {viewer.kind === 'pdf' && (
              <Box component="iframe" src={viewer.url} title="pdf" sx={{ display: 'block', width: '100%', height: '85vh', border: 0 }} />
            )}
            {viewer.kind === 'text' && (
              <Box component="pre" sx={{ m: 0, p: 2, width: '100%', maxHeight: '85vh', overflow: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{viewer.text}</Box>
            )}
            {viewer.kind === 'other' && (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2">プレビュー未対応の種類です。右上のダウンロードボタンから保存できます。</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </FormControl>
  );
}

// 単一選択 (CheckSelect のデザイン踏襲, Checkbox 無し)
export const SingleSelect = (props) => {
  const [value, setValue] = React.useState('');

  React.useEffect(() => {
    if (props && (props.value !== undefined && props.value !== null)) {
      setValue(props.value);
    } else {
      setValue('');
    }
  }, [props.value]);

  const handleChange = (event) => {
    const v = event.target.value;
    setValue(v);
    // react-hook-form register 済みなら setValue 経由で外部更新 (親から渡される想定)
    if (props.setValue && props.id) {
      props.setValue(props.id, v, { shouldDirty: true });
    }
  };

  // レンダリング
  return (
    <FormControl variant="standard" fullWidth>
      <ThemeLabel shrink htmlFor={props.id} id={`${props.id}_label`}>{props.label}</ThemeLabel>
      <Select
        labelId={`${props.id}_label`}
        id={props.id}
        value={value}
        onChange={handleChange}
        input={<ThemeInput id={props.id} {...props.register(props.id)}/>}
        displayEmpty={true}
        renderValue={(selected) => {
          if (selected === '' || selected === null || selected === undefined) {
            return <Typography textAlign="left" fontSize={14} color={(theme) => theme.palette.text.disabled}>{props.placeholder}</Typography>;
          }
          const opt = (props.options || []).find(o => o.id === selected);
          return opt ? opt.name : '';
        }}
      > 
        {(props.options || []).map(opt => (
          <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// 単一選択 (CheckFilledSelect のデザイン踏襲, Checkbox 無し)
export const SingleFilledSelect = (props) => {
  const [value, setValue] = React.useState('');

  React.useEffect(() => {
    if (props && (props.value !== undefined && props.value !== null)) {
      setValue(props.value);
    } else {
      setValue('');
    }
  }, [props.value]);

  const handleChange = (event) => {
    const v = event.target.value;
    setValue(v);
    if (props.onChange) props.onChange(v); // 親に通知
    // react-hook-form register 済みなら setValue 経由で外部更新 (親から渡される想定)
    if (props.setValue && props.id) {
      props.setValue(props.id, v, { shouldDirty: true });
    }
  };

  return (
    <FormControl variant="standard" fullWidth>
      <ThemeFilledText
        variant="standard"
        id={props.id}
        label={props.label}
        select
        {...(props.register ? props.register(props.id) : {})}
        value={value}
        onChange={handleChange}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start" sx={{mx:0}}></InputAdornment>,
            endAdornment: <InputAdornment position="end" sx={{mx:1}}></InputAdornment>
          },
          select: {
            multiple: false,
            displayEmpty: true,
            renderValue: (selected) => {
              if (selected === '' || selected === null || selected === undefined) {
                return <Typography textAlign="left" fontSize={14} color={(theme) => theme.palette.text.disabled}>{props.placeholder}</Typography>;
              }
              const opt = (props.options || []).find(o => o.id === selected);
              return opt ? opt.name : '';
            }
          }
        }}
      >
        {(props.options || []).map(opt => (
          <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
        ))}
      </ThemeFilledText>
    </FormControl>
  );
};

// 各種ボタン
export const PrimaryButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  width: 150,
  height: 50,
  borderRadius: '30px',
}));

export const SecondButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  width: 150,
  height: 50,
  borderRadius: '30px',
}));

export const SecondColorButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

export const GrayButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#888888',
  '&:hover': {
    backgroundColor: '#666666',
  },
  width: 150,
  height: 50,
  borderRadius: '30px',
}));

export const GrayColorButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#888888',
  '&:hover': {
    backgroundColor: '#666666',
  },
}));

export const RoundButton = styled(Button)(({ theme }) => ({
  width: 150,
  height: 50,
  borderRadius: '30px',
}));

export const BlackButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText('#000000'),
  backgroundColor: '#000000',
  '&:hover': {
    backgroundColor: '#333333',
  },
  height: 50,
  borderRadius: '30px',
}));

export const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText('#000000'),
  backgroundColor: '#000000',
  '&:hover': {
    backgroundColor: '#333333',
  },
}));

export const ColorTextButton = styled(Button)(({ theme }) => ({
  color: '#000000',
  '&:hover': {
    backgroundColor: '#cccccc',
  },
}));

export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
